import React, {useState, useCallback} from 'react';
import {ArrowLeft} from 'lucide-react';
import {AdminService} from '@/shared/services';
import {getAdminHost} from '@/shared/utils/adminHost';
import {ORG_PROVIDER_RETURN_TOKEN_KEY, ADMIN_RETURN_STATE_KEY} from '@/shared/constants/providerSwitch';
import Button from '@/shared/components/Button';

/**
 * Reads the org-admin return-token from sessionStorage and returns a header
 * action button ("Back to Org Console") that can be passed to DashboardLayout.
 *
 * Returns `null` when there is no return context (normal provider login).
 */
export function useOrgReturnAction(): React.ReactNode {
    const [returnToken, setReturnToken] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        return sessionStorage.getItem(ORG_PROVIDER_RETURN_TOKEN_KEY);
    });
    const [returning, setReturning] = useState(false);

    const handleReturn = useCallback(async () => {
        if (!returnToken) return;
        setReturning(true);
        try {
            let storedState: Record<string, unknown> | null = null;
            try {
                const raw = sessionStorage.getItem(ADMIN_RETURN_STATE_KEY);
                storedState = raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
            } catch {
                storedState = null;
            }
            const params = new URLSearchParams();
            const fallbackOrgId = storedState?.orgId ? String(storedState.orgId) : null;
            if (fallbackOrgId) params.set('orgId', fallbackOrgId);
            if (storedState?.orgDetailTab) params.set('tab', String(storedState.orgDetailTab));
            if (storedState?.orgFilter != null) params.set('orgFilter', String(storedState.orgFilter));
            if (storedState?.providerFilter != null) params.set('providerFilter', String(storedState.providerFilter));

            const adminHost = getAdminHost(window.location.hostname);
            if (window.location.hostname === adminHost) {
                params.set('orgConsole', '1');
            }
            const redirectTarget = params.toString() ? `/admin?${params.toString()}` : '/admin';
            sessionStorage.removeItem(ORG_PROVIDER_RETURN_TOKEN_KEY);
            setReturnToken(null);

            await AdminService.exchangeOrgConsoleSwitchToken(returnToken);
            window.location.assign(`${window.location.origin}${redirectTarget}`);
        } catch {
            setReturning(false);
        }
    }, [returnToken]);

    if (!returnToken) return null;

    return (
        <Button
            variant="secondary"
            size="md"
            onClick={handleReturn}
            disabled={returning}
            iconLeft={<ArrowLeft className="h-3.5 w-3.5" />}>
            {returning ? 'Returning...' : 'Back to Org Console'}
        </Button>
    );
}
